import React, { useState } from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { TenantUserPermission } from '@recurrency/core-api-schema/dist/common/enums';
import { UpdateItemLocationInfoType } from '@recurrency/core-api-schema/dist/purchasing/postUpdateItemLocationInfo';

import { Button } from 'components/Button';
import { DividerLine } from 'components/DividerLine';
import { FlexSpace } from 'components/FlexSpace';
import { ExternalLink } from 'components/Links';
import { Modal } from 'components/Modal';
import { PropertyListItem } from 'components/PropertyListItem';
import { ItemLocationSettingsAuditLog } from 'components/recipes/AuditLog/ItemLocationSettingsAuditLog';
import { Tabs } from 'components/Tabs';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { pluralize, splitIdNameStr } from 'utils/formatting';
import { createSubmissionNotification } from 'utils/submissionNotification';
import { PlanningModalType, track, TrackEvent } from 'utils/track';

import { MultiUpdateReplenishmentSettingsTable } from './MultiUpdateReplenishmentSettingsTable';
import { PositiveNumberInput } from './PositiveNumberInput';
import { getPlanningListSelectionFromFilterSelection, MultiModalSelection, PlanningBulkUpdateToFields } from './utils';

export function MultiMinOrderQuantityModal({
  selection,
  onClose,
  searchIndexReload,
  clearSelection,
}: {
  selection: MultiModalSelection;
  onClose: (isSubmit: boolean) => void;
  searchIndexReload?: () => void;
  clearSelection?: () => void;
}) {
  const { activeTenant } = useGlobalApp();
  const singleRecord = selection.type === 'list' && selection.items.length === 1 ? selection.items[0] : undefined;
  const [minOrderQuantity, setMinOrderQuantity] = useState<number | undefined>(
    () => singleRecord?.min_order_quantity ?? undefined,
  );

  const handleMinOrderQuantityUpdate = async () => {
    onClose(true);

    const submitNotification = createSubmissionNotification({
      entityName: 'Minimum order quantity changes',
      submittingMessage: 'Updating minimum order quantity changes',
      // 30 sec + 30 sec per 5k records
      expectedWaitSeconds: 30 + Math.round(selection.count / 5_000) * 30,
      erpType: activeTenant.erpType,
    });
    try {
      if (selection.type === 'filter') {
        selection = await getPlanningListSelectionFromFilterSelection(selection);
      }

      await coreApiFetch(schemas.purchasing.postUpdateItemLocationInfo, {
        pathParams: {
          updateType: UpdateItemLocationInfoType.MinOrderQuantity,
        },
        bodyParams: {
          updates: selection.items.map((record) => ({
            itemUid: record.item_uid,
            locationId: splitIdNameStr(record.location).foreignId,
            minOrderQuantity: minOrderQuantity || null,
          })),
        },
      });

      track(TrackEvent.DemandPlanning_MinMaxInfo_Submission, {
        modalType: PlanningModalType.MultiMinOrderQuantity,
        itemCount: selection.count,
      });
      clearSelection?.();
      searchIndexReload?.();
      submitNotification.success({
        successMessage: `Successfully updated minimum order quantity for ${pluralize(
          selection.count,
          'item',
          'items',
        )}`,
      });
    } catch (err) {
      submitNotification.error(err);
    }
  };

  const isSubmitDisabled =
    (minOrderQuantity !== undefined && (!Number.isInteger(minOrderQuantity) || minOrderQuantity < 0)) ||
    (singleRecord && minOrderQuantity === (singleRecord?.min_order_quantity ?? undefined));

  return (
    <Modal
      visible
      title={
        singleRecord
          ? `Update Minimum Order Quantity for Item #${singleRecord.item_id} at Location ${singleRecord.location}`
          : `Update Minimum Order Quantity for ${pluralize(selection.count, 'Item', 'Items', true)}`
      }
      onCancel={() => onClose(false)}
      destroyOnClose
      footer={
        <>
          <Button onClick={() => onClose(false)}>Cancel</Button>
          <Button
            onClick={handleMinOrderQuantityUpdate}
            type="primary"
            permissionsRequired={[TenantUserPermission.ActionEditItemSupplierDetails]}
            disabled={isSubmitDisabled}
          >
            Update
          </Button>
        </>
      }
      width={900}
    >
      {/* MOQ Input */}
      <FlexSpace direction="column" gap={16}>
        <FlexSpace direction="column" gap={8}>
          <span>
            Setting a minimum order quantity value will update the maximum whenever necessary to ensure the minimum
            order quantity is met. Learn more about minimum order quantity{' '}
            <ExternalLink to="https://help.recurrency.com/hc/en-us/articles/36272105067547-Minimum-Order-Quantities">
              here
            </ExternalLink>
            .
          </span>
          <PropertyListItem
            label="Minimum Order Quantity"
            value={
              <PositiveNumberInput allowClear requiresInteger onChange={setMinOrderQuantity} value={minOrderQuantity} />
            }
          />
        </FlexSpace>
      </FlexSpace>
      <DividerLine />

      {/* Settings to Change */}
      <MultiUpdateReplenishmentSettingsTable
        selection={selection}
        bulkUpdate={{
          type: 'minOrderQuantity',
          minOrderQuantity,
        }}
      />

      {/* Audit Log */}
      {singleRecord && (
        <>
          <DividerLine marginTop={42} />
          <Tabs
            tabs={[
              {
                header: 'Audit Log',
                infoTooltip: `Minimum order quantity updates for Item #${singleRecord.item_id} at Location #${singleRecord.location}`,
                content: (
                  <ItemLocationSettingsAuditLog
                    itemUid={singleRecord.item_uid}
                    locationId={splitIdNameStr(singleRecord.location).foreignId}
                    fields={PlanningBulkUpdateToFields.minOrderQuantity}
                  />
                ),
              },
            ]}
          />
        </>
      )}
    </Modal>
  );
}
